import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TID } from "@/lib/testIds";
import { GoogleLogo, ShieldCheck, Envelope, LockKey } from "@phosphor-icons/react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const loginEmail = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/auth/login", { email, password });
      await refresh();
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen flex flex-col">
      <Navbar />
      <section className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-between border-r border-border p-12 relative">
          <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
          <div className="relative">
            <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-6">Log in</div>
            <h1 className="font-heading font-black tracking-tighter text-5xl xl:text-6xl leading-[0.95] mb-6">
              Continue where<br/> you left off.
            </h1>
            <p className="text-muted-foreground max-w-md leading-relaxed">
              Access your requests, quotes, active orders and secure downloads.
            </p>
          </div>
          <div className="relative text-xs font-mono text-muted-foreground">
            <div className="flex items-center gap-2"><ShieldCheck size={14} /> HttpOnly session · 7-day expiry</div>
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="max-w-sm w-full">
            <div className="text-xs font-mono tracking-widest-2 uppercase text-muted-foreground mb-3">Sign in</div>
            <h2 className="font-heading font-bold text-3xl mb-2">Welcome to ExactReach</h2>
            <p className="text-sm text-muted-foreground mb-8">Sign in with Google or your email and password.</p>

            <Button
              data-testid={TID.auth.googleBtn}
              onClick={loginGoogle}
              className="w-full h-12 rounded-sm bg-foreground hover:bg-foreground/90 text-background flex items-center justify-center gap-3"
            >
              <GoogleLogo size={18} weight="bold" />
              Continue with Google
            </Button>

            <div className="my-6 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground">
              <span className="h-px flex-1 bg-border" />
              or with email
              <span className="h-px flex-1 bg-border" />
            </div>

            <form onSubmit={loginEmail} className="space-y-4" data-testid="login-email-form">
              <div>
                <Label htmlFor="login-email" className="text-xs">Email</Label>
                <div className="relative mt-1.5">
                  <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="login-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-9 rounded-sm h-11"
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="login-password" className="text-xs flex items-center justify-between">
                <span>Password</span>
                <Link to="/forgot-password" className="text-[11px] text-muted-foreground hover:text-foreground underline font-normal" data-testid="login-forgot-link">Forgot?</Link>
              </Label>
                <div className="relative mt-1.5">
                  <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-9 rounded-sm h-11"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="w-full h-11 rounded-sm"
                data-testid="login-submit-btn"
              >
                {busy ? "Signing in…" : "Sign in with email"}
              </Button>
            </form>

            <p className="text-sm text-muted-foreground mt-6">
              New to ExactReach?{" "}
              <Link to="/register" className="underline font-medium text-foreground" data-testid="login-goto-register">
                Create an account
              </Link>
            </p>

            <div className="mt-6 text-xs text-muted-foreground leading-relaxed">
              By continuing you agree to our <Link to="/legal/terms" className="underline">Terms</Link> and <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
